// Картинка «Поделиться» для профиля анкеты /tutors/<slug>: 1080×1350.
//
// КАК УСТРОЕНО. Сервер (маршрут /api/map/share/<slug>?lang=…) собирает SVG
// этим модулем: имя, до 4 предметов, район, строка «Məni Yolutap xəritəsində
// tapın», короткая ссылка, знак и надпись Yolutap. Фото анкеты, если есть,
// вшивается в SVG как data:-URI (бакет приватный, внешнюю ссылку картинка
// не загрузит). PNG делает браузер (share-raster.ts): вшивает в SVG шрифт
// Nunito Sans из /fonts/, рисует на canvas и отдаёт файл. Генератора
// растровых картинок на сервере в проекте нет, тянуть resvg/satori ради одной
// картинки не стали.
//
// Телефона, WhatsApp, email и адреса на картинке нет: только то, что и так
// видно на публичной странице (имя, предметы, район).
//
// Без серверных импортов: модуль читают маршрут, страница и
// scripts/verify-tutor-seo.mts.
import { MAP_COPY, TUTORS_PATH } from "@/components/partners/map-copy";
import { BAKU_CITY, DISTRICTS, labelOf, subjectItemsFor } from "@/data/map-taxonomy";
import { DISTRICT_LABELS } from "@/lib/map-places";
import type { PublicPartner } from "@/lib/map-public.functions";
import { initialsOf } from "@/lib/map-photo";

type Lang = "az" | "ru" | "en";

export const SHARE_W = 1080;
export const SHARE_H = 1350;
export const SHARE_MAX_SUBJECTS = 4;

/** Маршрут SVG-картинки. lang — язык страницы, с которой нажали «Поделиться». */
export const shareCardPath = (slug: string, lang: Lang) => `/api/map/share/${encodeURIComponent(slug)}?lang=${lang}`;

/** Короткая ссылка на картинке: домен + путь, без https://. */
export const shortProfileLink = (slug: string, lang: Lang) => `yolutap.com${TUTORS_PATH[lang]}/${slug}`;

export const SHARE_COPY: Record<
  Lang,
  {
    button: string;
    title: string;
    tagline: string;
    download: string;
    copy: string;
    copied: string;
    copyFailed: string;
    send: string;
    preparing: string;
    error: string;
    hint: string;
    close: string;
    kind: Record<PublicPartner["kind"], string>;
  }
> = {
  az: {
    button: "Paylaş",
    title: "Profili paylaş",
    tagline: "Məni Yolutap xəritəsində tapın",
    download: "Şəkli yüklə",
    copy: "Linki kopyala",
    copied: "Link kopyalandı",
    copyFailed: "Kopyalamaq alınmadı. Linki seçib özünüz kopyalayın:",
    send: "Göndər",
    preparing: "Şəkil hazırlanır…",
    error: "Şəkli hazırlamaq alınmadı. Linki yenə də kopyalaya bilərsiniz.",
    hint: "Şəkli Instagram hekayəsinə qoyun, linki isə profilinizin bio hissəsinə əlavə edin.",
    close: "Bağla",
    kind: { tutor: "Repetitor", club: "Dərnək" },
  },
  ru: {
    button: "Поделиться",
    title: "Поделиться профилем",
    tagline: "Найдите меня на карте Yolutap",
    download: "Скачать картинку",
    copy: "Копировать ссылку",
    copied: "Ссылка скопирована",
    copyFailed: "Не удалось скопировать. Выделите ссылку и скопируйте сами:",
    send: "Отправить",
    preparing: "Готовим картинку…",
    error: "Не удалось подготовить картинку. Ссылку можно скопировать.",
    hint: "Картинку — в сторис Instagram, ссылку — в шапку профиля.",
    close: "Закрыть",
    kind: { tutor: "Репетитор", club: "Кружок" },
  },
  en: {
    button: "Share",
    title: "Share this profile",
    tagline: "Find me on the Yolutap map",
    download: "Download image",
    copy: "Copy link",
    copied: "Link copied",
    copyFailed: "Couldn't copy. Select the link and copy it yourself:",
    send: "Send",
    preparing: "Preparing the image…",
    error: "Couldn't prepare the image. You can still copy the link.",
    hint: "Post the image to your Instagram story and put the link in your bio.",
    close: "Close",
    kind: { tutor: "Tutor", club: "Club" },
  },
};

// ─── Данные картинки ──────────────────────────────────────────────────

export type CardPartner = Pick<PublicPartner, "kind" | "name" | "slug" | "subjects" | "address_mode" | "district" | "travel_districts" | "city" | "city_name">;

export type ShareCardData = {
  lang: Lang;
  kind: PublicPartner["kind"];
  kindLabel: string;
  name: string;
  initials: string;
  subjects: string[];
  moreSubjects: number;
  place: string;
  tagline: string;
  link: string;
  /** data:-URI фото или null — тогда инициалы. */
  photo: string | null;
};

export function placeOf(p: CardPartner, lang: Lang): string {
  const c = MAP_COPY[lang];
  if (p.address_mode === "online") return c.online;
  // Вне Баку — город вместо районов (cityless строки до миграции — Баку).
  if (p.city && p.city !== BAKU_CITY) {
    const name = p.city_name?.[lang] ?? p.city;
    return p.address_mode === "travel" ? `${c.travelsTo}: ${name}` : name;
  }
  if (p.address_mode === "travel")
    return p.travel_districts.length ? `${c.travelsTo}: ${p.travel_districts.map((d) => labelOf(DISTRICT_LABELS, d, lang)).join(", ")}` : "";
  return p.district ? labelOf(DISTRICT_LABELS, p.district, lang) : "";
}

export function shareCardData(p: CardPartner, lang: Lang, photo: string | null = null): ShareCardData {
  const items = subjectItemsFor(p.kind);
  const all = p.subjects.map((s) => labelOf(items, s, lang).replace(/\s*\([^)]*\)/g, ""));
  return {
    lang,
    kind: p.kind,
    kindLabel: SHARE_COPY[lang].kind[p.kind],
    name: p.name.trim(),
    initials: initialsOf(p.name),
    subjects: all.slice(0, SHARE_MAX_SUBJECTS),
    moreSubjects: Math.max(0, all.length - SHARE_MAX_SUBJECTS),
    place: placeOf(p, lang),
    tagline: SHARE_COPY[lang].tagline,
    link: shortProfileLink(p.slug, lang),
    photo,
  };
}

// ─── SVG ──────────────────────────────────────────────────────────────

export const INK = "#143F4B";
export const TEAL = "#1E5A6B";
export const PAPER = "#FBFAF7";
export const AMBER = "#F0A63A";
export const GREEN = "#4BAE8C";
export const PURPLE = "#7A5AAE";

/** Сюда share-raster.ts вставляет @font-face со шрифтом из /fonts/. */
export const FONT_SLOT = "/*FONTS*/";
export const FAMILY = "'Nunito Sans', 'Segoe UI', Roboto, Arial, sans-serif";

export const xml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[c]!);

/**
 * Ширина строки в пикселях — оценка по классам букв (Nunito Sans, жирный).
 * Точная не нужна: длинные строки переносятся с запасом, а последняя
 * линия при перехлёсте ужимается textLength.
 */
export function approxWidth(text: string, size: number): number {
  let w = 0;
  for (const ch of text) {
    if (" .,:;'’!|".includes(ch)) w += 0.28;
    else if ("iIıİjlft()[]-·".includes(ch)) w += 0.33;
    else if ("mwMWШЩЖЮФЫшщжюмфы".includes(ch)) w += 0.88;
    else if (ch !== ch.toLowerCase()) w += 0.68;
    else w += 0.57;
  }
  return w * size;
}

/** Перенос по словам в maxLines строк; не влезло — многоточие в последней. */
export function wrap(text: string, size: number, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (let i = 0; i < words.length; i++) {
    const next = cur ? `${cur} ${words[i]}` : words[i];
    if (approxWidth(next, size) <= maxWidth || !cur) {
      cur = next;
      continue;
    }
    lines.push(cur);
    cur = words[i];
    if (lines.length === maxLines) {
      cur = "";
      const last = lines[maxLines - 1];
      lines[maxLines - 1] = `${last}…`;
      break;
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, maxLines);
}

/**
 * Предметы по строкам: « · » только между предметами внутри строки, не в
 * конце. Не влезли в maxLines — остаток уходит в «+N» последней строки.
 */
export function wrapItems(items: string[], more: number, size: number, maxWidth: number, maxLines: number): string[] {
  const lines: string[][] = [];
  let i = 0;
  for (; i < items.length; i++) {
    const cur = lines[lines.length - 1];
    if (cur && approxWidth([...cur, items[i]].join(" · "), size) <= maxWidth) cur.push(items[i]);
    else if (lines.length < maxLines) lines.push([items[i]]);
    else break;
  }
  const rest = more + (items.length - i);
  const out = lines.map((l) => l.join(" · "));
  if (rest && out.length) out[out.length - 1] += ` +${rest}`;
  return out;
}

/** <text> по центру; если оценка шире maxWidth — ужать textLength, чтобы точно влезло. */
function centered(text: string, y: number, size: number, weight: number, fill: string, maxWidth: number, extra = ""): string {
  const fit = approxWidth(text, size) > maxWidth ? ` textLength="${maxWidth}" lengthAdjust="spacingAndGlyphs"` : "";
  return `<text x="${SHARE_W / 2}" y="${y}" text-anchor="middle" font-size="${size}" font-weight="${weight}" fill="${fill}"${fit}${extra}>${xml(text)}</text>`;
}

/** Знак Yolutap (четыре пазла) — те же пути, что YolutapIcon. */
export function icon(x: number, y: number, size: number): string {
  const k = size / 240;
  return `<g transform="translate(${x} ${y}) scale(${k})">
<g transform="translate(20, 20) rotate(-6, 50, 50)"><path fill="${TEAL}" d="M0 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L65 100 C65 118 35 118 35 100 L0 100 Z"/></g>
<g transform="translate(125, 14) rotate(8, 50, 50)"><path fill="${AMBER}" d="M0 0 L100 0 L100 100 L65 100 C65 118 35 118 35 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"/></g>
<g transform="translate(14, 130) rotate(-3, 50, 50)"><path fill="${GREEN}" d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L0 100 Z"/></g>
<g transform="translate(130, 132) rotate(6, 50, 50)"><path fill="${PURPLE}" d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"/></g>
</g>`;
}

/**
 * Надпись Yolutap по Brand Handoff v2.0: Nunito Sans 700, вся чернильная,
 * янтарная точка на «1 часе» первой «o» с кольцом цвета фона.
 * Доли em — замер Nunito Sans 700 в Chrome (canvas measureText): «Yo» с
 * кернингом 1.111em, «o» 0.576em, letter-spacing -0.025em → «o» занимает
 * 0.510–1.086em. Точка .18em, right -.02em, top .06em от рамки «o» при
 * line-height .85 (верх рамки ≈ 0.754em над базовой линией).
 */
export function wordmark(x: number, baseline: number, size: number): string {
  const r = 0.09 * size;
  const cx = x + (1.086 + 0.02) * size - r;
  const cy = baseline - (0.754 - 0.06) * size + r;
  return `<text x="${x}" y="${baseline}" font-size="${size}" font-weight="700" letter-spacing="${-0.025 * size}" fill="${INK}">Yolutap</text>
<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(r + 0.045 * size).toFixed(1)}" fill="${PAPER}"/>
<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${AMBER}"/>`;
}

export const PIN = "M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z";

export function shareCardSvg(d: ShareCardData): string {
  const W = SHARE_W;
  const H = SHARE_H;
  const textMax = W - 2 * 96;
  const parts: string[] = [];

  // Фон и пазлы-украшения по углам.
  parts.push(`<rect width="${W}" height="${H}" fill="${PAPER}"/>`);
  parts.push(`<g opacity="0.16"><g transform="translate(860 -40) rotate(14) scale(2.2)"><path fill="${AMBER}" d="M0 0 L100 0 L100 100 L65 100 C65 118 35 118 35 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"/></g>
<g transform="translate(-90 560) rotate(-10) scale(1.6)"><path fill="${GREEN}" d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L0 100 Z"/></g></g>`);

  // Шапка: знак + надпись.
  parts.push(icon(88, 92, 84));
  parts.push(wordmark(186, 158, 64));

  // Раскладка: крупное фото, если всё влезает над плашкой; длинное имя или
  // много предметов — фото меньше, потом меньше строк предметов.
  const cx = W / 2;
  const nameLines = wrap(d.name, 78, textMax, 2);
  const BOTTOM = 995; // ниже — плашка со ссылкой
  const variants = [
    { R: 150, cy: 420, subj: 3 },
    { R: 120, cy: 390, subj: 3 },
    { R: 120, cy: 390, subj: 2 },
    { R: 100, cy: 370, subj: 2 },
    { R: 100, cy: 370, subj: 1 },
  ];
  let pick = variants[variants.length - 1];
  let subjLines: string[] = [];
  for (const v of variants) {
    const lines = d.subjects.length ? wrapItems(d.subjects, d.moreSubjects, 42, textMax, v.subj) : [];
    const lastName = v.cy + v.R + 80 + 92 + (nameLines.length - 1) * 88;
    const lastSubj = lines.length ? lastName + 94 + (lines.length - 1) * 56 : lastName;
    const bottom = d.place ? lastSubj + 70 : lastSubj;
    pick = v;
    subjLines = lines;
    if (bottom <= BOTTOM) break;
  }
  const { R, cy } = pick;

  // Фото или инициалы.
  const club = d.kind === "club";
  const shape = club
    ? `<rect x="${cx - R}" y="${cy - R}" width="${2 * R}" height="${2 * R}" rx="${Math.round(R * 0.3)}"/>`
    : `<circle cx="${cx}" cy="${cy}" r="${R}"/>`;
  parts.push(`<defs><clipPath id="av">${shape}</clipPath></defs>`);
  parts.push(
    club
      ? `<rect x="${cx - R - 12}" y="${cy - R - 12}" width="${2 * R + 24}" height="${2 * R + 24}" rx="${Math.round(R * 0.3) + 10}" fill="${AMBER}"/>`
      : `<circle cx="${cx}" cy="${cy}" r="${R + 12}" fill="${AMBER}"/>`,
  );
  if (d.photo) {
    parts.push(`<g clip-path="url(#av)"><rect x="${cx - R}" y="${cy - R}" width="${2 * R}" height="${2 * R}" fill="${TEAL}"/><image href="${d.photo}" x="${cx - R}" y="${cy - R}" width="${2 * R}" height="${2 * R}" preserveAspectRatio="xMidYMid slice"/></g>`);
  } else {
    const fs = Math.round(R * 0.8);
    parts.push(`<g clip-path="url(#av)"><rect x="${cx - R}" y="${cy - R}" width="${2 * R}" height="${2 * R}" fill="${TEAL}"/></g>`);
    parts.push(centered(d.initials, cy + Math.round(fs * 0.35), fs, 800, PAPER, 2 * R - 40));
  }

  // Вид, имя.
  let y = cy + R + 80;
  const locale = d.lang === "az" ? "az" : d.lang === "ru" ? "ru" : "en";
  parts.push(centered(d.kindLabel.toLocaleUpperCase(locale), y, 34, 800, TEAL, textMax, ` letter-spacing="5"`));
  y += 92;
  for (const line of nameLines) {
    parts.push(centered(line, y, 78, 800, INK, textMax));
    y += 88;
  }

  // Предметы: « · » между предметами, строк — сколько позволила раскладка.
  y += 6;
  for (const line of subjLines) {
    parts.push(centered(line, y, 42, 600, TEAL, textMax));
    y += 56;
  }

  // Район с булавкой.
  if (d.place) {
    y += 14;
    const placeLine = wrap(d.place, 40, textMax - 60, 1)[0];
    const w = Math.min(approxWidth(placeLine, 40), textMax - 60);
    const px = cx - (w + 52) / 2;
    parts.push(`<path transform="translate(${px.toFixed(1)} ${y - 36}) scale(1.75)" fill="${AMBER}" d="${PIN}"/>`);
    const fit = approxWidth(placeLine, 40) > textMax - 60 ? ` textLength="${textMax - 60}" lengthAdjust="spacingAndGlyphs"` : "";
    parts.push(`<text x="${(px + 52).toFixed(1)}" y="${y}" font-size="40" font-weight="700" fill="${INK}"${fit}>${xml(placeLine)}</text>`);
  }

  // Плашка внизу: строка-приглашение и короткая ссылка.
  const by = 1046;
  parts.push(`<rect x="64" y="${by}" width="${W - 128}" height="236" rx="40" fill="${INK}"/>`);
  parts.push(centered(d.tagline, by + 98, 48, 800, PAPER, W - 128 - 96));
  parts.push(centered(d.link, by + 168, 34, 700, AMBER, W - 128 - 80));

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
<style>${FONT_SLOT}
text{font-family:${FAMILY}}</style>
${parts.join("\n")}
</svg>
`;
}

// ─── Ответ маршрута ───────────────────────────────────────────────────

const SLUG_RE = /^[a-z0-9-]{1,80}$/;

export type ShareCardDeps = {
  /** Анкета из map_partners_public (только опубликованные); photo_url — путь в бакете. */
  loadPartner: (slug: string) => Promise<PublicPartner | null>;
  /** data:-URI фото анкеты или null. Не бросает: сбой фото — картинка с инициалами. */
  loadPhoto: (p: PublicPartner) => Promise<string | null>;
};

const notFound = () => new Response("Not found", { status: 404, headers: { "cache-control": "no-store" } });

/** GET /api/map/share/<slug>?lang=az|ru|en → SVG 1080×1350; неопубликованная или нет такой — 404. */
export async function shareCardResponse(slug: string, langParam: string | null, deps: ShareCardDeps): Promise<Response> {
  if (!SLUG_RE.test(slug)) return notFound();
  let p: PublicPartner | null;
  try {
    p = await deps.loadPartner(slug);
  } catch (e) {
    console.error("[/api/map/share] read failed", e);
    return new Response("Unavailable", { status: 503, headers: { "cache-control": "no-store" } });
  }
  if (!p) return notFound();
  const lang: Lang = langParam === "az" || langParam === "ru" || langParam === "en" ? langParam : p.lang;
  const photo = await deps.loadPhoto(p).catch(() => null);
  return new Response(shareCardSvg(shareCardData(p, lang, photo)), {
    status: 200,
    headers: {
      "content-type": "image/svg+xml; charset=utf-8",
      // Как у фото: снятая анкета пропадает за 10 минут.
      "cache-control": "public, max-age=600",
      "x-content-type-options": "nosniff",
      "content-security-policy": "default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:",
    },
  });
}

/** Байты → data:-URI (в воркере нет Buffer). */
export function bytesToDataUri(bytes: Uint8Array, mime: string): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${mime};base64,${btoa(bin)}`;
}
